import type { Vector2 } from "./geometry";
import type { MiterSourceEdge } from "./MiterSourceEdge";
import type { MiterSourceNode } from "./MiterSourceNode";

interface MiterSkeletonNodeShape {
  position: Vector2;
  velocity: Vector2;
  readonly incident: MiterSkeletonEdge[];
  otherIncident(exclude: MiterSkeletonEdge): MiterSkeletonEdge | undefined;
}

// In the debug UI the user can click-drag a skeleton edge to
// change its width. However, the width must be updated on the
// underlying source edge's leftWidth / rightWidth, not the
// skeleton edge itself — skeleton edges are rebuilt from the
// source on every edit. This tag is how the UI spots the
// draggable side-wavefront edges and traces them back to the
// (sourceEdge, side) pair whose width needs to change. Caps and
// chords aren't draggable; they carry no back-pointer. Nothing
// in the algorithm itself reads this field.

export type MiterSkeletonEdgeSource =
  | { kind: "side"; sourceEdge: MiterSourceEdge; side: "left" | "right" }
  | { kind: "cap"; sourceNode: MiterSourceNode }
  | { kind: "chord" };

export class MiterSkeletonEdge {
  a: MiterSkeletonNodeShape;
  b: MiterSkeletonNodeShape;
  normal: Vector2;
  width: number;
  readonly source: MiterSkeletonEdgeSource;

  constructor(
    a: MiterSkeletonNodeShape,
    b: MiterSkeletonNodeShape,
    normal: Vector2,
    width: number,
    source: MiterSkeletonEdgeSource,
  ) {
    this.a = a;
    this.b = b;
    this.normal = { x: normal.x, y: normal.y };
    this.width = width;
    this.source = source;
  }
}
